
import Vue from "vue";
import { mapActions, mapGetters, mapMutations } from "vuex";
import {
  GET_USER_DETAILS,
  USER_PROFILE_BY_ID
} from "@/store/modules/auth/constants";
import { ROOT_NOTIFICATION } from "@/store/modules/root/constants";
import {
  HYGEN_SESSION_ID,
  SESSION_INITIALIZED_SUCCESSFULLY,
  SPEAK_HYGEN_BOT
} from "@/store/modules/digital_human/constants";
import {
  AI_INTERVIEW_INVITES,
  INIT_AI_INTERVIEW_INVITES
} from "@/store/modules/recruiter_ai_interviews/constants";
import DigitalHuman from "@/components/candidate/interviews/DigitalHuman.vue";
import {
  get_session_result,
  InterviewTranscriptMessage,
  ML_INTERVIEW_WS_URL,
  WsTranscriptMessage,
  upsert_interview_report
} from "@/store/apis/ml_interview";
import { get_job_by_id } from "@/store/apis/common";

export default Vue.extend({
  name: "CandidateAiInterviewSession",
  components: {
    DigitalHuman
  },
  data() {
    return {
      botAvatar: require("@/assets/icons/linear/bot.svg"),
      interviewStarted: false,
      interviewFinished: false,
      isPaused: false,
      durationSeconds: 20 * 60,
      elapsedSeconds: 0,
      timerRef: null as ReturnType<typeof setInterval> | null,
      questionTimerRef: null as ReturnType<typeof setInterval> | null,
      currentQuestionIndex: 0,
      recognition: null as any,
      isListening: false,
      speechRecognitionSupported: false,
      liveTranscript: "",
      finalVoiceTranscript: "",
      /** Text after Stop speaking; not sent until Submit answer */
      pendingAnswerText: "",
      shouldKeepListening: false,
      shouldAutoStartListening: false,
      /** Resolves when recognition.onend runs after a forced stop (interview complete). */
      speechFinalizeResolver: null as null | (() => void),
      micErrorMessage: "",
      showEndConfirmDialog: false,
      showProcessingDialog: false,
      hygenReady: false,
      ws: null as WebSocket | null,
      pendingBotSpeechQueue: [] as string[],
      threadId: "",
      interviewUserId: "",
      resumeText: "",
      jdText: "",
      askedQuestionIds: [] as string[],
      transcriptRows: [] as {
        sender: "bot" | "candidate";
        avatar: string;
        message: string;
        time: string;
      }[],
      staticCandidateReplies: [
        "Sure. I have strong backend experience building scalable APIs and distributed systems.",
        "Monolith keeps everything in one deployable unit, while microservices split domains into independent services.",
        "I reproduced the issue, isolated the failing path with logs, added tests, then shipped a safe fix.",
        "I focus on modular design, code reviews, clear naming, and automated tests.",
        "I align on requirements early, share progress often, and validate edge cases before release."
      ]
    };
  },
  computed: {
    ...mapGetters("auth", {
      user: GET_USER_DETAILS
    }),
    ...mapGetters("recruiter_ai_interviews", {
      aiInvites: AI_INTERVIEW_INVITES
    }),
    ...mapGetters("digital_human", {
      hygenSessionId: HYGEN_SESSION_ID,
      sessionInitializedSuccessfully: SESSION_INITIALIZED_SUCCESSFULLY
    }),
    formattedElapsed(): string {
      const minutes = Math.floor(this.elapsedSeconds / 60)
        .toString()
        .padStart(2, "0");
      const seconds = (this.elapsedSeconds % 60).toString().padStart(2, "0");
      return `${minutes}:${seconds}`;
    },
    formattedDuration(): string {
      const minutes = Math.floor(this.durationSeconds / 60)
        .toString()
        .padStart(2, "0");
      const seconds = (this.durationSeconds % 60).toString().padStart(2, "0");
      return `${minutes}:${seconds}`;
    },
    progressWidth(): string {
      const percentage = (this.elapsedSeconds / this.durationSeconds) * 100;
      return `${Math.min(100, Math.max(0, percentage))}%`;
    },
    candidateName(): string {
      if (!this.user?.first_name) return "Ahmad Al Mansouri";
      return `${this.user.first_name} ${this.user.last_name || ""}`.trim();
    },
    aiAvatar(): string {
      return this.botAvatar || require("@/assets/icons/linear/bot.svg");
    },
    candidateAvatar(): string {
      return this.user?.avatar_uri || require("@/assets/icons/linear/bot.svg");
    },
    transcriptItems(): {
      sender: "bot" | "candidate";
      avatar: string;
      message: string;
      time: string;
    }[] {
      return this.transcriptRows;
    },
    candidateAiInvite(this: any): any {
      const candidateId = Number(this.user?.id || 0);
      if (!candidateId) return null;
      const inviteId = String(this.$route?.query?.inviteId || "").trim();
      const routeJobId = Number(this.$route?.query?.jobId || 0);
      if (inviteId) {
        const exactInvite = (this.aiInvites || []).find((i: any) => {
          if (Number(i?.candidate_id || 0) !== candidateId) return false;
          if ((i?.interview_type || "ai") !== "ai") return false;
          if (String(i?.id || "") !== inviteId) return false;
          if (routeJobId && Number(i?.job_id || 0) !== routeJobId) return false;
          return true;
        });
        if (exactInvite) return exactInvite;
      }
      const invites = (this.aiInvites || []).filter((i: any) => {
        if (Number(i?.candidate_id || 0) !== candidateId) return false;
        if ((i?.interview_type || "ai") !== "ai") return false;
        return String(i?.status || "").toLowerCase() === "scheduled";
      });
      invites.sort(
        (a: any, b: any) =>
          new Date(b?.scheduled_at || 0).getTime() -
          new Date(a?.scheduled_at || 0).getTime()
      );
      return invites[0] || null;
    },
    hasVoiceDraft(): boolean {
      return (
        String(this.liveTranscript || "").trim().length > 0 ||
        String(this.finalVoiceTranscript || "").trim().length > 0
      );
    },
    hasPendingAnswerToSubmit(): boolean {
      return String(this.pendingAnswerText || "").trim().length > 0;
    },
    voiceDisplayHint(): string {
      if (this.isListening) {
        return (
          String(this.liveTranscript || "").trim() || "Listening… speak now."
        );
      }
      if (this.hasPendingAnswerToSubmit) {
        return String(this.pendingAnswerText);
      }
      return "Tap Start speaking, then Stop speaking. Review your text, then tap Submit answer.";
    },
    editableVoiceAnswer: {
      get(): string {
        if (this.isListening) {
          return String(this.liveTranscript || "").trim();
        }
        return String(this.pendingAnswerText || "").trim();
      },
      set(value: string) {
        if (this.isListening) return;
        this.pendingAnswerText = String(value || "");
      }
    },
    canRedoLastAnswer(): boolean {
      const rows = this.transcriptRows;
      if (!rows.length) return false;
      return rows[rows.length - 1].sender === "candidate";
    },
    canClearOrRedo(): boolean {
      return this.hasPendingAnswerToSubmit || this.canRedoLastAnswer;
    },
    redoOrClearLabel(): string {
      if (this.hasPendingAnswerToSubmit) return "Clear answer";
      if (this.canRedoLastAnswer) return "Re-answer";
      return "Clear";
    }
  },
  methods: {
    ...mapActions("digital_human", {
      speakHygenBot: SPEAK_HYGEN_BOT
    }),
    ...mapActions("auth", {
      fetchProfileById: USER_PROFILE_BY_ID
    }),
    ...mapActions("recruiter_ai_interviews", {
      initInvites: INIT_AI_INTERVIEW_INVITES
    }),
    ...mapMutations({
      set_root_notification: ROOT_NOTIFICATION
    }),
    async onHygenReady() {
      this.hygenReady = true;
      await this.flushPendingBotSpeech();
      if (!this.interviewStarted) {
        await this.startInterview();
      }
    },
    async flushPendingBotSpeech() {
      if (!this.hygenSessionId || !this.sessionInitializedSuccessfully) return;
      if (!this.pendingBotSpeechQueue.length) return;
      const queue = [...this.pendingBotSpeechQueue];
      this.pendingBotSpeechQueue = [];
      for (const text of queue) {
        if (!text) continue;
        const ok = await this.trySpeakWithRetry(String(text), 5, 500);
        if (!ok) {
          // put back and stop trying for now
          this.pendingBotSpeechQueue.unshift(text);
          break;
        }
      }
    },
    async trySpeakWithRetry(text: string, attempts = 5, delayMs = 500): Promise<boolean> {
      if (!text) return true;
      for (let i = 0; i < attempts; i += 1) {
        if (!this.hygenSessionId || !this.sessionInitializedSuccessfully) return false;
        try {
          const speakResp = await this.speakHygenBot({
            session_id: this.hygenSessionId,
            text
          });
          if (speakResp) return true;
        } catch (_e) {
          // continue retry
        }
        if (i < attempts - 1) {
          await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
      }
      return false;
    },
    async startInterview() {
      const inviteDurationMinutes = Number(this.candidateAiInvite?.duration_minutes || 0);
      this.durationSeconds =
        Number.isFinite(inviteDurationMinutes) && inviteDurationMinutes > 0
          ? inviteDurationMinutes * 60
          : 20 * 60;
      this.interviewStarted = true;
      this.interviewFinished = false;
      this.isPaused = false;
      this.elapsedSeconds = 0;
      this.currentQuestionIndex = 0;
      this.transcriptRows = [];
      this.pendingAnswerText = "";
      this.askedQuestionIds = [];
      this.startTimer();
      await this.openInterviewSocket();
      this.set_root_notification("Interview started with live AI interviewer");
    },
    endInterview() {
      if (this.interviewStarted && !this.interviewFinished) {
        this.showEndConfirmDialog = true;
        return;
      }
      this.confirmEndInterview();
    },
    confirmEndInterview() {
      this.showEndConfirmDialog = false;
      this.stopTimer();
      this.stopQuestionTimer();
      this.interviewFinished = true;
      this.stopListening();
      if (this.ws) this.ws.close();
      const inviteId = String(this.candidateAiInvite?.id || "").trim();
      if (inviteId) {
        this.$router.push({
          name: "candidate-ai-interview-details",
          params: { inviteId }
        });
        return;
      }
      this.$router.push({ name: "candidate-ai-interview" });
    },
    startTimer() {
      this.stopTimer();
      this.timerRef = setInterval(() => {
        if (this.elapsedSeconds < this.durationSeconds) {
          this.elapsedSeconds += 1;
        } else {
          this.stopTimer();
          this.stopQuestionTimer();
        }
      }, 1000);
    },
    startQuestionTimer() {
      this.stopQuestionTimer();
      this.questionTimerRef = setInterval(async () => {
        if (!this.interviewStarted || this.isPaused) return;
        await this.pushNextQuestion();
      }, 35000);
    },
    stopQuestionTimer() {
      if (this.questionTimerRef) {
        clearInterval(this.questionTimerRef);
        this.questionTimerRef = null;
      }
    },
    getCurrentTimeLabel(): string {
      return new Date().toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit"
      });
    },
    async pushNextQuestion() {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
      if (!this.threadId) return;

      const candidateReply =
        this.staticCandidateReplies[this.currentQuestionIndex] ||
        "Thanks for the question. Here is my response based on my previous work.";

      window.setTimeout(() => {
        if (!this.interviewStarted || this.interviewFinished) return;
        this.transcriptRows.push({
          sender: "candidate",
          avatar: this.user?.avatar_uri || this.botAvatar,
          message: candidateReply,
          time: this.getCurrentTimeLabel()
        });
        this.ws?.send(
          JSON.stringify({
            user_id: this.interviewUserId,
            thread_id: this.threadId,
            message: candidateReply
          })
        );
      }, 8000);

      this.currentQuestionIndex += 1;
    },
    async openInterviewSocket() {
      await this.prepareInterviewContext();
      if (this.ws) this.ws.close();

      this.ws = new WebSocket(ML_INTERVIEW_WS_URL);
      this.ws.onopen = () => {
        this.ws?.send(
          JSON.stringify({
            user_id: this.interviewUserId,
            resume_text: this.resumeText,
            jd_text: this.jdText,
            message: "start"
          })
        );
      };
      this.ws.onmessage = async (event: MessageEvent) => {
        try {
          const payload = JSON.parse(event.data);
          if (payload?.status === "error") {
            this.set_root_notification(payload.message || "Interview service error");
            return;
          }
          const aiText = payload?.result;
          if (aiText) {
            // New question only — do not clear draft on the closing message (same payload as is_complete),
            // or the candidate's last spoken answer is wiped before we can flush it to the server.
            if (!payload?.is_complete) {
              this.pendingAnswerText = "";
              this.finalVoiceTranscript = "";
              this.liveTranscript = "";
            }
            this.transcriptRows.push({
              sender: "bot",
              avatar: this.botAvatar,
              message: aiText,
              time: this.getCurrentTimeLabel()
            });
            if (this.hygenSessionId && this.sessionInitializedSuccessfully) {
              const ok = await this.trySpeakWithRetry(String(aiText), 5, 500);
              if (!ok) {
                this.pendingBotSpeechQueue.push(String(aiText));
              }
            } else {
              this.pendingBotSpeechQueue.push(String(aiText));
            }
            await this.flushPendingBotSpeech();
            if (!payload?.is_complete) {
              this.shouldAutoStartListening = true;
              this.startListening();
            }
          }
          if (payload?.thread_id) this.threadId = payload.thread_id;
          if (payload?.is_complete) {
            await this.completeInterviewFlow(payload);
          }
        } catch (e) {
          this.set_root_notification("Failed to process interview response");
        }
      };
      this.ws.onerror = () => {
        this.set_root_notification("Interview socket connection failed");
      };
    },
    async resolveJobDescriptionById(jobId: number): Promise<string> {
      if (!jobId) return "";
      try {
        const resp = await get_job_by_id(jobId);
        const payload = resp?.data?.data || resp?.data || {};
        return String(payload?.job_description || payload?.description || "").trim();
      } catch (_e) {
        // ignore
      }
      return "";
    },
    async prepareInterviewContext() {
      const fullName = `${this.user?.first_name || "candidate"} ${
        this.user?.last_name || ""
      }`.trim();
      this.interviewUserId = `${this.user?.id || "candidate"}_${Date.now()}`;
      let profileText = "";
      let jdDescription = "";
      const candidateId = Number(this.user?.id || 0);
      if (candidateId) {
        try {
          const profile = await this.fetchProfileById(candidateId);
          profileText = JSON.stringify(profile?.profile || profile || "");
        } catch (_e) {
          profileText = "";
        }
      }
      const invite = this.candidateAiInvite;
      const jobId = Number(invite?.job_id || 0);
      if (jobId) {
        jdDescription = await this.resolveJobDescriptionById(jobId);
      }
      this.resumeText =
        profileText ||
        localStorage.getItem("candidate_resume_text") ||
        `${fullName} - Senior software engineer with backend and architecture experience.`;
      this.jdText =
        jdDescription ||
        String(invite?.job_description || "").trim() ||
        String(invite?.job_role || "").trim() ||
        localStorage.getItem("candidate_jd_text") ||
        "Senior Software Engineer role focused on backend systems, APIs, and collaboration.";
    },
    captureableVoiceAnswer(): string {
      return (
        String(this.pendingAnswerText || "").trim() ||
        String(this.liveTranscript || "").trim() ||
        String(this.finalVoiceTranscript || "").trim()
      );
    },
    flushPendingVoiceToServer() {
      const pending = this.captureableVoiceAnswer();
      if (!pending) return;
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN || !this.threadId) return;
      this.transcriptRows.push({
        sender: "candidate",
        avatar: this.user?.avatar_uri || this.botAvatar,
        message: pending,
        time: this.getCurrentTimeLabel()
      });
      this.sendCandidateAnswer(pending);
      this.pendingAnswerText = "";
      this.finalVoiceTranscript = "";
      this.liveTranscript = "";
    },
    submitPendingAnswer() {
      const text = String(this.pendingAnswerText || "").trim();
      if (!text || this.isListening || !this.speechRecognitionSupported) return;
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN || !this.threadId) return;
      this.transcriptRows.push({
        sender: "candidate",
        avatar: this.user?.avatar_uri || this.botAvatar,
        message: text,
        time: this.getCurrentTimeLabel()
      });
      this.sendCandidateAnswer(text);
      this.pendingAnswerText = "";
    },
    clearOrRedoAnswer() {
      if (!this.speechRecognitionSupported || this.isListening) return;
      if (this.hasPendingAnswerToSubmit) {
        this.pendingAnswerText = "";
        return;
      }
      if (this.canRedoLastAnswer) {
        this.transcriptRows.pop();
        this.pendingAnswerText = "";
        this.finalVoiceTranscript = "";
        this.liveTranscript = "";
        this.set_root_notification(
          "Previous answer removed. Record again, then tap Submit answer."
        );
      }
    },
    waitForSpeechFinalizeAfterStop(maxMs: number): Promise<void> {
      return new Promise((resolve) => {
        if (!this.isListening) {
          resolve();
          return;
        }
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          resolve();
        };
        this.speechFinalizeResolver = finish;
        this.shouldKeepListening = false;
        try {
          this.recognition?.stop();
        } catch (_e) {
          this.speechFinalizeResolver = null;
          finish();
          return;
        }
        window.setTimeout(() => {
          if (this.speechFinalizeResolver === finish) {
            this.speechFinalizeResolver = null;
            finish();
          }
        }, maxMs);
      });
    },
    async completeInterviewFlow(payload: any) {
      this.shouldKeepListening = false;
      // Never clear transcript buffers before stopping recognition — onend must see live text
      // to fill pendingAnswerText; clearing first was dropping the last answer.
      await this.waitForSpeechFinalizeAfterStop(1600);
      this.flushPendingVoiceToServer();
      this.pendingAnswerText = "";
      this.finalVoiceTranscript = "";
      this.liveTranscript = "";
      this.isListening = false;

      await new Promise((resolve) => setTimeout(resolve, 900));
      const transcript: WsTranscriptMessage[] = await this.fetchTranscriptFromResult();
      const candidateAnswersCount = (transcript || []).filter(
        (item: any) => String(item?.role || "").toLowerCase() === "user"
      ).length;
      const maxQuestions = 10;
      if (candidateAnswersCount < maxQuestions) {
        this.interviewFinished = false;
        return;
      }
      this.showProcessingDialog = true;

      this.stopQuestionTimer();
      this.stopTimer();
      this.interviewFinished = true;
      if (this.ws) this.ws.close();

      const normalizedTranscript: InterviewTranscriptMessage[] = (transcript || [])
        .map((item: any): InterviewTranscriptMessage | null => {
          const role = String(item?.role || "").toLowerCase();
          const content = String(item?.content || "").trim();
          if (!content) return null;
          return {
            speaker: role === "assistant" ? "interviewer" : "candidate",
            text: content
          };
        })
        .filter((item): item is InterviewTranscriptMessage => item !== null);
      if (this.user?.id) {
        await upsert_interview_report({
          candidate_id: Number(this.user.id),
          interview_type: "ai",
          job_id: Number(this.candidateAiInvite?.job_id || 0) || null,
          thread_id: this.threadId || null,
          ai_transcript: normalizedTranscript,
          question_bank: [],
          status: "completed"
        });
      }
      const inviteId = String(this.candidateAiInvite?.id || "").trim();
      if (inviteId) {
        this.$router.push({
          name: "candidate-ai-interview-details",
          params: { inviteId }
        });
        return;
      }
      this.$router.push({ name: "candidate-ai-interview" });
    },
    async fetchTranscriptFromResult(): Promise<WsTranscriptMessage[]> {
      if (!this.threadId) return [];
      try {
        const resp = await get_session_result(this.threadId, this.interviewUserId);
        return resp?.data?.transcript || [];
      } catch (e) {
        return [];
      }
    },
    initSpeechRecognition() {
      const SpeechRecognition =
        (window as any).SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        this.speechRecognitionSupported = false;
        return;
      }
      this.speechRecognitionSupported = true;
      const recognition = new SpeechRecognition();
      recognition.lang = "en-US";
      recognition.interimResults = true;
      recognition.continuous = true;
      recognition.maxAlternatives = 1;

      recognition.onstart = () => {
        this.isListening = true;
      };
      recognition.onresult = (event: any) => {
        let interimTranscript = "";
        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          const chunk = (event.results[i][0].transcript || "").trim();
          if (!chunk) continue;
          if (event.results[i].isFinal) {
            this.finalVoiceTranscript = `${this.finalVoiceTranscript} ${chunk}`.trim();
          } else {
            interimTranscript = `${interimTranscript} ${chunk}`.trim();
          }
        }
        this.liveTranscript = `${this.finalVoiceTranscript} ${interimTranscript}`.trim();
      };
      recognition.onend = () => {
        this.isListening = false;
        if (
          this.shouldKeepListening &&
          this.interviewStarted &&
          !this.interviewFinished &&
          !this.isPaused
        ) {
          window.setTimeout(() => this.startListening(), 100);
          return;
        }

        const spokenText = this.liveTranscript.trim();
        if (spokenText) {
          this.pendingAnswerText = spokenText;
        }
        this.finalVoiceTranscript = "";
        this.liveTranscript = "";
        const finalize = this.speechFinalizeResolver;
        if (finalize) {
          this.speechFinalizeResolver = null;
          finalize();
        }
      };
      recognition.onerror = (event: any) => {
        this.isListening = false;
        const errorType = String(event?.error || "").toLowerCase();
        const silentErrors = ["aborted", "no-speech"];
        if (!silentErrors.includes(errorType)) {
          this.shouldKeepListening = false;
          if (errorType === "not-allowed" || errorType === "service-not-allowed") {
            this.micErrorMessage =
              "Microphone permission is blocked. Allow mic access in browser site settings, then try Start speaking.";
          } else if (errorType === "audio-capture") {
            this.micErrorMessage =
              "No microphone was detected. Check your input device, then try Start speaking again.";
          } else if (errorType === "network") {
            this.micErrorMessage =
              "Speech recognition network error. Please retry Start speaking.";
          } else {
            this.micErrorMessage =
              "Voice capture was interrupted. Tap Start speaking again, or type your answer below.";
          }
          this.set_root_notification(this.micErrorMessage);
        }
        const res = this.speechFinalizeResolver;
        if (res) {
          this.speechFinalizeResolver = null;
          res();
        }
      };

      this.recognition = recognition;
    },
    sendCandidateAnswer(answer: string) {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN || !this.threadId) return;
      this.ws.send(
        JSON.stringify({
          user_id: this.interviewUserId,
          thread_id: this.threadId,
          message: answer
        })
      );
    },
    startListening() {
      if (!this.speechRecognitionSupported || !this.recognition) return;
      if (!this.interviewStarted || this.interviewFinished || this.isPaused) return;
      if (this.isListening) return;
      this.micErrorMessage = "";
      if (!this.shouldKeepListening) {
        this.shouldKeepListening = true;
        this.pendingAnswerText = "";
        this.finalVoiceTranscript = "";
        this.liveTranscript = "";
      }
      try {
        this.recognition.start();
      } catch (e: any) {
        const name = String(e?.name || "").toLowerCase();
        if (name !== "invalidstateerror") {
          this.shouldKeepListening = false;
          this.micErrorMessage =
            "Unable to start microphone. Check browser mic permission and try again.";
          this.set_root_notification(this.micErrorMessage);
        }
      }
    },
    stopListening() {
      this.shouldKeepListening = false;
      if (!this.recognition || !this.isListening) return;
      try {
        this.recognition.stop();
      } catch (e) {
        // ignore
      }
    },
    toggleVoiceAnswer() {
      if (this.isListening) this.stopListening();
      else this.startListening();
    },
    handleBeforeUnload(event: BeforeUnloadEvent) {
      if (this.interviewStarted && !this.interviewFinished) {
        event.preventDefault();
        event.returnValue = "";
      }
    },
    stopTimer() {
      if (this.timerRef) {
        clearInterval(this.timerRef);
        this.timerRef = null;
      }
    }
  },
  async created() {
    this.initSpeechRecognition();
    window.addEventListener("beforeunload", this.handleBeforeUnload);
    await this.initInvites();
    const inviteDurationMinutes = Number(this.candidateAiInvite?.duration_minutes || 0);
    this.durationSeconds =
      Number.isFinite(inviteDurationMinutes) && inviteDurationMinutes > 0
        ? inviteDurationMinutes * 60
        : 20 * 60;
  },
  watch: {
    hygenSessionId: {
      immediate: false,
      async handler(this: any) {
        await this.flushPendingBotSpeech();
      }
    },
    sessionInitializedSuccessfully: {
      immediate: false,
      async handler(this: any) {
        await this.flushPendingBotSpeech();
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.interviewStarted && !this.interviewFinished) {
      const shouldLeave = window.confirm(
        "Your interview progress will be lost. Do you want to leave?"
      );
      if (!shouldLeave) {
        next(false);
        return;
      }
    }
    next();
  },
  beforeDestroy() {
    this.stopTimer();
    this.stopQuestionTimer();
    this.stopListening();
    if (this.ws) this.ws.close();
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
  }
});
